import { Component, Input, Output, EventEmitter, OnInit, OnChanges, SimpleChanges } from '@angular/core';

// Interface definitions for expression functions
export interface FunctionParameter {
  name: string;
  label: string;
  type: 'text' | 'dateformat' | 'routedetail' | 'headerdetail' | 'messagebody' | 'number';
  required: boolean;
  isDroppable?: boolean;
  placeholder?: string;
}

export interface ExpressionFunction {
  name: string;
  displayName: string;
  category: string;
  description: string;
  parameters: FunctionParameter[];
  template: string;
}

export interface ExpressionFunctionsConfig {
  functions: ExpressionFunction[];
  dateFormats: string[];
  routeDetails: string[];
  headerDetails: string[];
  messageBodyDetails: string[];
}

@Component({
  selector: 'app-hl7-expression-builder',
  templateUrl: './expression-builder.component.html',
  styleUrls: ['./expression-builder.component.scss']
})
export class ExpressionBuilderComponent implements OnInit, OnChanges {
  @Input() hl7Path: string = '';
  @Input() config: ExpressionFunctionsConfig | null = null;
  @Input() initialExpression: string = '';
  @Output() expressionChange = new EventEmitter<string>();

  useExpression: boolean = false;
  selectedFunction: ExpressionFunction | null = null;
  parameterValues: Map<string, string> = new Map();
  generatedExpression: string = '';
  searchTerm: string = '';

  // Grouped functions by category
  groupedFunctions: Map<string, ExpressionFunction[]> = new Map();
  categories: string[] = [];

  // Default dropdown options (fallback if config not loaded)
  dateFormats: string[] = [
    'MMddyyyy', 'yyMMdd', 'MM/dd/yyyy', 'MM/dd/yy',
    'MM/dd/yyyy hh:mm:ss', 'MM/dd/yyyy HH:mm:ss',
    'yyyy-MM-dd', 'yyyyMMdd', 'HH:mm', 'yyyyMMddHHmmss'
  ];

  routeDetails: string[] = [
    'id', 'displayname', 'messagetype', 'sourceagentid', 'destinationagentid',
    'status', 'SEGUID', 'SENAME', 'DEGUID', 'DENAME', 'PAID', 'PANAME'
  ];

  headerDetails: string[] = [
    'transactionid', 'hash', 'messageid', 'processingParams', 'jobContext', 'mode'
  ];

  messageBodyDetails: string[] = [
    'fileid', 'orginalfilename', 'filename'
  ];

  constructor() {}

  ngOnInit(): void {
    this.initializeFromConfig();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['config'] && this.config) {
      this.initializeFromConfig();
    }
    if (changes['initialExpression'] && this.initialExpression) {
      this.parseInitialExpression();
    }
  }

  private initializeFromConfig(): void {
    if (this.config) {
      // Use config values
      if (this.config.dateFormats?.length > 0) {
        this.dateFormats = this.config.dateFormats;
      }
      if (this.config.routeDetails?.length > 0) {
        this.routeDetails = this.config.routeDetails;
      }
      if (this.config.headerDetails?.length > 0) {
        this.headerDetails = this.config.headerDetails;
      }
      if (this.config.messageBodyDetails?.length > 0) {
        this.messageBodyDetails = this.config.messageBodyDetails;
      }

      // Group functions by category
      this.groupFunctionsByCategory();
    }
  }

  private groupFunctionsByCategory(): void {
    this.groupedFunctions.clear();
    
    if (!this.config?.functions) return;

    for (const func of this.config.functions) {
      const category = func.category || 'Other';
      if (!this.groupedFunctions.has(category)) {
        this.groupedFunctions.set(category, []);
      }
      this.groupedFunctions.get(category)!.push(func);
    }

    this.categories = Array.from(this.groupedFunctions.keys()).sort();
  }

  private parseInitialExpression(): void {
    // Try to parse an existing expression to pre-populate the builder
    // This is a basic implementation - can be enhanced for complex expressions
    if (!this.initialExpression || !this.config?.functions) return;

    for (const func of this.config.functions) {
      if (this.initialExpression.startsWith(func.name + '(')) {
        this.useExpression = true;
        this.selectedFunction = func;
        // TODO: Parse parameter values from expression
        break;
      }
    }
  }

  toggleExpression(): void {
    if (!this.useExpression) {
      this.clearSelection();
    }
    this.emitExpression();
  }

  onFunctionSelect(event: Event): void {
    const select = event.target as HTMLSelectElement;
    const functionName = select.value;
    
    if (!functionName) {
      this.clearSelection();
      return;
    }

    this.selectedFunction = this.config?.functions.find(f => f.name === functionName) || null;
    this.parameterValues.clear();
    
    // Pre-populate droppable fields with HL7 path if available
    if (this.selectedFunction && this.hl7Path) {
      for (const param of this.selectedFunction.parameters) {
        if (param.isDroppable && this.parameterValues.size === 0) {
          // Only set the first droppable parameter
          this.parameterValues.set(param.name, this.hl7Path);
          break;
        }
      }
    }
    
    this.updateGeneratedExpression();
  }

  onParameterChange(paramName: string, value: string): void {
    this.parameterValues.set(paramName, value);
    this.updateGeneratedExpression();
  }

  getParameterValue(paramName: string): string {
    return this.parameterValues.get(paramName) || '';
  }

  private updateGeneratedExpression(): void {
    if (!this.selectedFunction) {
      this.generatedExpression = '';
      this.emitExpression();
      return;
    }

    let expression = this.selectedFunction.template;
    
    for (const param of this.selectedFunction.parameters) {
      const value = this.parameterValues.get(param.name) || '';
      // Replace placeholder with actual value
      expression = expression.replace(`{${param.name}}`, value);
    }

    this.generatedExpression = expression;
    this.emitExpression();
  }

  private emitExpression(): void {
    if (this.useExpression && this.generatedExpression) {
      this.expressionChange.emit(this.generatedExpression);
    } else {
      this.expressionChange.emit('');
    }
  }

  private clearSelection(): void {
    this.selectedFunction = null;
    this.parameterValues.clear();
    this.generatedExpression = '';
  }

  getDropdownOptions(type: string): string[] {
    switch (type) {
      case 'dateformat':
        return this.dateFormats;
      case 'routedetail':
        return this.routeDetails;
      case 'headerdetail':
        return this.headerDetails;
      case 'messagebody':
        return this.messageBodyDetails;
      default:
        return [];
    }
  }

  isDropdownType(type: string): boolean {
    return ['dateformat', 'routedetail', 'headerdetail', 'messagebody'].includes(type);
  }

  getFilteredFunctions(): ExpressionFunction[] {
    if (!this.config?.functions) return [];
    
    if (!this.searchTerm) {
      return this.config.functions;
    }

    const term = this.searchTerm.toLowerCase();
    return this.config.functions.filter(f => 
      f.displayName.toLowerCase().includes(term) ||
      f.name.toLowerCase().includes(term) ||
      f.category.toLowerCase().includes(term)
    );
  }

  insertHl7Path(paramName: string): void {
    if (this.hl7Path) {
      this.parameterValues.set(paramName, this.hl7Path);
      this.updateGeneratedExpression();
    }
  }

  isValid(): boolean {
    if (!this.useExpression) return true;
    if (!this.selectedFunction) return false;

    // Check all required parameters have values
    for (const param of this.selectedFunction.parameters) {
      if (param.required && !this.parameterValues.get(param.name)) {
        return false;
      }
    }
    return true;
  }
}
